import { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../app/store.ts';
import { api } from '../data/api.ts';
import { derivePreview, deriveTitle } from '../data/markdown.ts';
import { matches, parseQuery, score } from '../lib/search.ts';
import { onIndexReady, searchIds } from '../search/client.ts';
import type { LocalNote } from '../data/types.ts';

/**
 * Die eingebettete Suche für Zeus, das Dashboard des Betreibers
 * (docs/KONZEPT.md, Abschnitt 21).
 *
 * Zeus lädt diese Ansicht in einen Rahmen und schickt Suchbegriffe per
 * `postMessage` hinein. Alles, was den Rahmen verlässt, sind der Suchbegriff und die
 * Zahl der Treffer — kein Titel, kein Inhalt, kein Schlüssel. Der Vault wird im
 * Rahmen selbst entsperrt, mit demselben Dialog wie in der App; der Schlüssel liegt
 * damit im Athene-Ursprung und nirgends sonst.
 *
 * Nachrichten (alle über `window.postMessage`, nie mit `targetOrigin: "*"`):
 *
 *   Zeus → Athene   { type: 'athene:suche',   q: string }
 *   Athene → Zeus   { type: 'athene:bereit',  entsperrt: boolean }   einmal beim Laden
 *   Athene → Zeus   { type: 'athene:treffer', q: string, anzahl: number }
 *
 * Angenommen wird nur, was vom Elternfenster kommt und dessen Origin in der Liste
 * des Servers steht — derselben Liste, die er in `frame-ancestors` schreibt.
 */

export const EMBED_PATH = '/embed/suche';

/** Ob diese Seite als eingebettete Suche läuft — entschieden am Pfad, einmal beim Laden. */
export function isEmbed(): boolean {
  return location.pathname === EMBED_PATH;
}

type Props = {
  phase: 'setup' | 'locked' | 'ready';
  /** Der Entsperr-Dialog, solange der Vault zu ist. */
  lock?: JSX.Element;
};

type Suche = { type: 'athene:suche'; q: string };

function isSuche(data: unknown): data is Suche {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { type?: unknown }).type === 'athene:suche' &&
    typeof (data as { q?: unknown }).q === 'string'
  );
}

/** Der Origin des Einbetters, so wie der Browser ihn beim Laden mitgab — oder nichts. */
function referrerOrigin(): string | null {
  try {
    return document.referrer ? new URL(document.referrer).origin : null;
  } catch {
    return null;
  }
}

/** Nur in einem fremden Rahmen gibt es jemanden, dem sich etwas melden ließe. */
function eingebettet(): boolean {
  return window.parent !== window;
}

export function EmbedSuche({ phase, lock }: Props): JSX.Element {
  const { notes, documents, funde } = useApp();
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<{ ids: string[]; fuzzy: string[] } | null>(null);
  const [origins, setOrigins] = useState<string[] | null>(null);
  /** Der Index meldet sich, wenn er steht — danach lohnt derselbe Begriff ein zweites Mal. */
  const [indexTick, setIndexTick] = useState(0);
  /** Von wem die letzte Suche kam: Dorthin geht die Trefferzahl, und nur dorthin. */
  const lastOrigin = useRef<string | null>(null);
  const bereitGemeldet = useRef(false);

  // Die erlaubten Einbetter kommen vom Server, nicht aus dem Bündel — sie sind
  // Betriebskonfiguration. Der eigene Ursprung ist immer dabei, genau wie `'self'`
  // in der Kopfzeile; so lässt sich die Ansicht auch von Athene selbst aus prüfen.
  // Antwortet der Server nicht, bleibt es beim eigenen Ursprung: lieber eine stumme
  // Einbettung als eine, die auf Zuruf von irgendwoher reagiert.
  useEffect(() => {
    let alive = true;
    void api
      .embedConfig()
      .then((config) => {
        if (alive) setOrigins([location.origin, ...config.origins]);
      })
      .catch(() => {
        if (alive) setOrigins([location.origin]);
      });
    return () => {
      alive = false;
    };
  }, []);

  // Suchbegriffe vom Elternfenster. Die Prüfung in dieser Reihenfolge: erst der
  // Absender, dann der Inhalt — ein fremder Rahmen soll nicht einmal erfahren, ob
  // seine Nachricht die richtige Form hatte.
  useEffect(() => {
    if (!origins) return undefined;
    const onMessage = (event: MessageEvent<unknown>): void => {
      if (event.source !== window.parent || !origins.includes(event.origin)) return;
      if (!isSuche(event.data)) return;
      lastOrigin.current = event.origin;
      setQ(event.data.q);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [origins]);

  // Einmal „bereit", sobald klar ist, ob der Vault offen ist. Die Adresse dafür ist
  // der Verweis, mit dem der Rahmen geladen wurde — und nur, wenn er in der Liste
  // steht. Ein Elternfenster, das nicht auf der Liste steht, bekommt gar nichts.
  useEffect(() => {
    if (!origins || bereitGemeldet.current || !eingebettet()) return;
    const target = referrerOrigin();
    if (!target || !origins.includes(target)) return;
    bereitGemeldet.current = true;
    window.parent.postMessage({ type: 'athene:bereit', entsperrt: phase === 'ready' }, target);
  }, [origins, phase]);

  // Der Index füllt sich nach dem Entsperren erst — beim ersten Mal aus den Notizen,
  // sonst aus dem OPFS. Ein Begriff, der schon wartet, wird deshalb nach dem Aufbau
  // noch einmal gestellt; und weil die Notizen nach dem Bereit-Signal in Häppchen
  // nachkommen, ein drittes Mal kurz danach.
  useEffect(() => {
    if (phase !== 'ready') return undefined;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const off = onIndexReady(() => {
      setIndexTick((n) => n + 1);
      timer = setTimeout(() => setIndexTick((n) => n + 1), 2_500);
    });
    return () => {
      off();
      if (timer) clearTimeout(timer);
    };
  }, [phase]);

  const parsed = useMemo(() => parseQuery(q), [q]);
  const searching = phase === 'ready' && q.trim() !== '';

  useEffect(() => {
    if (!searching) {
      setHits(null);
      return undefined;
    }
    let alive = true;
    const timer = setTimeout(() => {
      void searchIds(q, 50).then((result) => {
        if (alive) setHits(result);
      });
    }, 100);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [q, searching, notes.length, indexTick]);

  // Dieselbe Trefferliste wie in der App: Notizen, Dokumente und Gesammeltes, über
  // den Index geordnet — und ohne Index die einfache Textsuche als Rückfallebene.
  const pool = useMemo(() => {
    if (parsed.only === 'archiv') return documents;
    if (parsed.only === 'notizen') return notes;
    return [...notes, ...documents, ...funde];
  }, [notes, documents, funde, parsed.only]);

  const visible = useMemo<LocalNote[]>(() => {
    if (!searching) return [];
    const lebendig = pool.filter((note) => note.deletedAt === null);
    if (hits) {
      const rank = new Map(hits.ids.map((id, position) => [id, position]));
      return lebendig.filter((note) => rank.has(note.id)).sort((a, b) => rank.get(a.id)! - rank.get(b.id)!);
    }
    return lebendig
      .filter((note) => matches(note, parsed))
      .sort((a, b) => score(b, parsed) - score(a, parsed) || b.updatedAt - a.updatedAt);
  }, [pool, parsed, searching, hits]);

  // Nach jeder Suche die Zahl zurück an den, der gefragt hat. Nur die Zahl: Was
  // gefunden wurde, bleibt im Rahmen.
  useEffect(() => {
    if (!searching || !eingebettet() || !lastOrigin.current) return;
    window.parent.postMessage({ type: 'athene:treffer', q, anzahl: visible.length }, lastOrigin.current);
  }, [searching, q, visible]);

  if (phase !== 'ready') {
    return <div className="embed embed-locked">{lock}</div>;
  }

  const notizen = visible.filter((note) => !note.dokument && !note.fund);
  const dokumente = visible.filter((note) => note.dokument);
  const gesammelt = visible.filter((note) => note.fund);
  const groups: { label: string; items: LocalNote[] }[] = [];
  if (notizen.length > 0) groups.push({ label: `${notizen.length} Treffer`, items: notizen });
  if (dokumente.length > 0) groups.push({ label: `${dokumente.length} in den Dokumenten`, items: dokumente });
  if (gesammelt.length > 0) groups.push({ label: `${gesammelt.length} im Gesammelten`, items: gesammelt });

  return (
    <div className="embed">
      <div className="embed-head">
        <input
          autoFocus
          className="embed-input"
          type="search"
          placeholder="Suchen …"
          aria-label="Suchen"
          value={q}
          onChange={(event) => setQ(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setQ('');
          }}
        />
      </div>
      <div className="rows">
        {!searching && (
          <p className="empty">
            Volltext, <span className="key">tag:</span> <span className="key">folder:</span>{' '}
            <span className="key">in:archiv</span> — dieselbe Suche wie in der App.
          </p>
        )}
        {searching && visible.length === 0 && (
          <p className="empty">
            Nichts gefunden.{hits && hits.fuzzy.length > 0 ? ` Meintest du ${hits.fuzzy.join(', ')}?` : ''}
          </p>
        )}
        {groups.map((group) => (
          <div key={group.label}>
            <p className="group label">{group.label}</p>
            {group.items.map((note) => (
              // Ein Treffer öffnet die Notiz in der App — im obersten Fenster, nicht
              // im Rahmen: Dort gäbe es weder Platz noch Sinn für den Editor.
              <a key={note.id} className="row embed-row" href={`/#note=${note.id}`} target="_top">
                <span className="row-title">
                  {deriveTitle(note.content)}
                  {note.dokument && <span className="row-kind">Archiv</span>}
                </span>
                <span className="row-preview">
                  {derivePreview(note.content) ||
                    (note.dokument
                      ? note.dokument.erkannt.slice(0, 140).replace(/\s+/g, ' ') || 'Noch kein Text'
                      : 'Leere Notiz')}
                </span>
              </a>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
